import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import Imgix from 'shared/components/imgix';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';

type SpecialsCategoryCardProps = {
  category: DispensaryMenuCategory;
  href: string;
};

export function SpecialsCategoryCard({ category, href }: SpecialsCategoryCardProps): JSX.Element {
  const { dispensary } = useDispensary();
  const { label, cardImage } = category;
  const image = cardImage ?? getCategoryPhoto(dispensary, category);

  return (
    <CardLink href={href} data-testid='specials-category-card'>
      <Label>{label}</Label>
      <ImageContainer>
        <StyledImage
          src={image}
          disableSrcSet
          imgixParams={{ height: 224 }}
          htmlAttributes={{ alt: `${label} category image` }}
        />
      </ImageContainer>
    </CardLink>
  );
}

const CardLink = styled(Link)`
  background: ${({ theme }) => theme.colors?.white ?? 'white'};
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  width: 151px;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  @media (min-width: 960px) {
    gap: 28px;
    padding: 20px;
    width: 184px;
  }
`;

const ImageContainer = styled.div`
  aspect-ratio: 640 / 340;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled(Imgix)`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  line-height: 20px;
  width: 100%;

  @media (min-width: 960px) {
    font-size: 20px;
    line-height: 24px;
  }
`;
